import SearchIcon from "@mui/icons-material/Search";
import { Box, Button, TextField, styled } from "@mui/material";
import { ReactNode, useEffect, useState } from "react";
import { HeaderFormatEnum } from "../../../components/common-table/enum/header-format";
import TableComponent, {
    ITableColumn,
} from "../../../components/common-table/table-component";
import useDebounce from "../../../hooks/use-debounce";
import { useAppDispatch, useAppSelector } from "../../../redux/store";
import { getListDetailsPath } from "../../../shared/component/get-list-details-path";
import RenderLink from "../../../shared/component/render-link";
import { IWithClassName, IWithThemeProps } from "../../../theme/default.theme";
import { ChangeLogColumns } from "../enum/change-log-columns";
import { IChangeLogList } from "../interface/dto/change-log.dto";
import {
    changeLogExportAction,
    changeLogSetPaginationAction,
    changeLogSetSearchQueryAction,
    changeLogSetSortingAction,
} from "../redux/change-log.actions";
import { ITEMS_PER_PAGE_ALL } from "../selectors/filters-and-pagination";
import { changeLogTableColumns } from "../service/change-log-table-columns";

const TABLE_COLUMNS = [
    ...changeLogTableColumns.map((column) => ({
        title: column.name,
        id: column.id,
        sortable: true,
        width: column.width,
    })),
];

const ChangeLogTable = ({ className }: IWithClassName) => {
    const dispatch = useAppDispatch();
    const { changeLogs, pagination, isLoading } = useAppSelector(
        (state) => state.changeLog,
    );
    const [query, setQuery] = useState<string | undefined>(undefined);

    const debouncedQuery = useDebounce(query);

    const handleExportClick = () => {
        dispatch(changeLogExportAction());
    };

    useEffect(() => {
        if (query !== undefined) {
            dispatch(changeLogSetSearchQueryAction(debouncedQuery ?? ""));
        }
    }, [debouncedQuery, dispatch]);

    const handleRowsPerPageChange = (rows: number) => {
        dispatch(changeLogSetPaginationAction(pagination.currentPage, rows));
    };

    const handlePageChange = (page: number) => {
        dispatch(changeLogSetPaginationAction(page, pagination.itemsPerPage));
    };

    const handleColumnOrder = (
        columnIndex: number,
        column: ITableColumn,
        direction: "asc" | "desc",
    ) => {
        dispatch(
            changeLogSetSortingAction(column.id as ChangeLogColumns, direction),
        );
    };

    const getCellContent = (list: IChangeLogList, columnKey: string) => {
        switch (columnKey) {
            case ChangeLogColumns.LIST_NAME:
                return (
                    <RenderLink
                        href={getListDetailsPath(
                            list[ChangeLogColumns.LIST_ID].toString(),
                        )}
                    >
                        {list[columnKey as keyof IChangeLogList]?.toString()}
                    </RenderLink>
                );
            default:
                return list[columnKey as keyof IChangeLogList]?.toString();
        }
    };

    const renderCell = (rowIndex: number, columnIndex: number): ReactNode => {
        const adjustedRowIndex =
            pagination.itemsPerPage === ITEMS_PER_PAGE_ALL
                ? rowIndex
                : rowIndex % pagination.itemsPerPage;

        const changeLog = changeLogs?.result?.[adjustedRowIndex];
        if (!changeLog) return null;

        const columnKey = TABLE_COLUMNS[columnIndex].id;
        const cellContent = getCellContent(changeLog, columnKey);
        return <span className={className}> {cellContent} </span>;
    };

    const renderNoDataMessage = () => {
        if (query && query.trim().length > 0) {
            return `No results found for "${query}". Try different search criteria.`;
        }
    };

    return (
        <Box className={className}>
            <div className="header">
                <Button
                    variant="outlined"
                    color="secondary"
                    onClick={handleExportClick}
                >
                    Export
                </Button>
            </div>
            <TableComponent
                columns={TABLE_COLUMNS}
                headerFormat={HeaderFormatEnum.PageSizeLeft}
                rowsPerPage={pagination.itemsPerPage}
                totalRows={changeLogs?.total ?? 0}
                currentPage={pagination.currentPage}
                onRowsPerPageChange={handleRowsPerPageChange}
                onPageChange={handlePageChange}
                onColumnOrder={handleColumnOrder}
                renderCell={renderCell}
                renderNoDataMessage={renderNoDataMessage}
                uniqueRowId={(rowIndex) => `${rowIndex}`}
                isLoading={isLoading}
                customHeader={
                    <div className="search-box-placeholder">
                        <TextField
                            variant="standard"
                            value={query ?? ""}
                            onChange={({ currentTarget }) =>
                                setQuery(currentTarget.value)
                            }
                            InputProps={{
                                endAdornment: (
                                    <SearchIcon className="search-icon" />
                                ),
                            }}
                        />
                    </div>
                }
            />
        </Box>
    );
};

export default styled(ChangeLogTable)(({ theme }: IWithThemeProps) => ({
    ".search-icon": {
        color: theme?.palette.text.primary,
    },
    a: {
        textDecoration: "none",
        color: theme?.palette.trackedListsTable.textColor,
    },
    ".header": {
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-start",
        gap: theme?.spacing(2),
        marginBottom: theme?.spacing(5),
    },
}));
